import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Alert, Badge, Button, Input, Pagination, Select, Table, goldToast } from "gold-ui-kit";
import AppShell from "../components/classic/AppShell";
import ClassicWindow from "../components/classic/ClassicWindow";
import GroupBox from "../components/classic/GroupBox";
import Toolbar, { ToolbarButton, ToolbarSeparator } from "../components/classic/Toolbar";
import { fetchEmployees } from "../store/slices/employees";
import { downloadResignationForm } from "../store/slices/employees/employeesApi";
import { fetchResignationNotices } from "../store/slices/employees/resignations/resignationsThunks";
import { useAuth } from "../context/AuthContext";
import { fetchBranchesNoPaginateApi } from "../services/branchApi";
import { toLocalDateKey } from "../utils/date";
import { RotateCw, Download, Search } from "lucide-react";

const blankForm = () => ({
  employeeId: "",
  hireDate: "",
  lastWorkDate: toLocalDateKey(),
});

const OPERATORS = [
  { value: 0, label: "Eşittir" },
  { value: 1, label: "Eşit Değildir" },
  { value: 2, label: "Büyüktür" },
  { value: 3, label: "Küçüktür" },
  { value: 4, label: "İçerir" },
];

const FIELDS = [
  { value: "employeeName", label: "Personel Adı" },
  { value: "employeeSurname", label: "Personel Soyadı" },
  { value: "status", label: "Durum" },
];

const STATUS_MAP = {
  1: { label: "Bekliyor", color: "warning" },
  2: { label: "Onaylandı", color: "success" },
  3: { label: "Reddedildi", color: "danger" },
};

const EMPTY_FILTER = { field: "", value: "", operator: "" };

const FormLabel = ({ children }) => <span className="text-xs font-semibold text-slate-700">{children}</span>;

const IstifaPage = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const employees = useSelector((s) => s.employees?.list || []);
  const { items, page, size, count, loading } = useSelector((s) => s.resignations);

  const [form, setForm] = useState(blankForm);
  const [branchInfo, setBranchInfo] = useState(null);
  const [branchLoading, setBranchLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [filter, setFilter] = useState({
    field: "employeeName",
    operator: 4,
    value: "",
  });

  const loadBranch = useCallback(async () => {
    if (!user?.branchId) return;
    setBranchLoading(true);
    try {
      const branches = await fetchBranchesNoPaginateApi();
      const userBranchId = String(user?.branchId || "").trim().toLowerCase();
      const matched = (branches || []).find((b) => String(b.id || "").trim().toLowerCase() === userBranchId);
      if (matched) setBranchInfo(matched);
    } catch (err) {
      console.error("Şube bilgileri yüklenemedi:", err);
    } finally {
      setBranchLoading(false);
    }
  }, [user?.branchId]);

  useEffect(() => {
    if (user?.branchId) {
      const year = new Date().getFullYear();
      const month = new Date().getMonth() + 1;
      dispatch(fetchEmployees({ branchId: user.branchId, year, month }));
      dispatch(fetchResignationNotices({ page: 1, size: 10, filters: EMPTY_FILTER }));
      loadBranch();
    }
  }, [dispatch, user?.branchId, loadBranch]);

  useEffect(() => {
    const empId = searchParams.get("employee");
    if (empId && employees.length > 0) {
      const emp = employees.find((e) => e.id === empId);
      if (emp) {
        setForm((f) => ({
          ...f,
          employeeId: emp.id,
          hireDate: emp.hireDate ? emp.hireDate.slice(0, 10) : "",
        }));
      }
    }
  }, [searchParams, employees]);

  const employeeOptions = useMemo(
    () =>
      employees
        .filter((e) => !e.exitDate && !e.ExitDate && !e.resignationDate && !e.ResignationDate)
        .map((e) => ({ value: e.id, label: `${e.name} ${e.surname}` })),
    [employees]
  );

  const setField = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "employeeId") {
        const emp = employees.find((e) => e.id === v);
        if (emp) next.hireDate = emp.hireDate ? emp.hireDate.slice(0, 10) : "";
      }
      return next;
    });
  };

  const validate = () => {
    if (!form.employeeId) {
      goldToast.warning("Lütfen personel seçiniz.");
      return false;
    }
    if (!form.lastWorkDate) {
      goldToast.warning("Lütfen çıkış tarihini giriniz.");
      return false;
    }
    return true;
  };

  const activeFilter = () => (filter.value ? filter : EMPTY_FILTER);

  const handleDownload = async () => {
    if (!validate()) return;
    setDownloading(true);
    try {
      const today = toLocalDateKey();
      await downloadResignationForm(form.employeeId, form.hireDate || today, form.lastWorkDate || today, branchInfo?.address || "");
      goldToast.success("İstifa formu indirildi.");
      dispatch(fetchResignationNotices({ page: 1, size, filters: activeFilter() }));
    } catch (err) {
      goldToast.error("Form indirilirken hata oluştu.");
    } finally {
      setDownloading(false);
    }
  };

  const handleSearch = () => dispatch(fetchResignationNotices({ page: 1, size, filters: activeFilter() }));

  const handlePageChange = (newPage) => dispatch(fetchResignationNotices({ page: newPage, size, filters: activeFilter() }));

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("tr-TR");
    } catch {
      return dateStr;
    }
  };

  const columns = [
    { key: "createdAt", dataIndex: "createdAt", title: "Tarih", render: (r) => formatDate(r.createdAt) },
    {
      key: "employee",
      dataIndex: "employeeName",
      title: "Personel",
      render: (r) => (
        <span className="font-medium text-slate-900">
          {r.employeeName} {r.employeeSurname}
        </span>
      ),
    },
    { key: "hireDate", dataIndex: "hireDate", title: "Giriş", render: (r) => formatDate(r.hireDate) },
    { key: "exitDate", dataIndex: "exitDate", title: "Çıkış", render: (r) => formatDate(r.exitDate) },
    {
      key: "status",
      dataIndex: "status",
      title: "Durum",
      align: "center",
      render: (r) => {
        const st = STATUS_MAP[r.status];
        return (
          <Badge size="sm" radius="full" variant="soft" color={st?.color || "default"}>
            {st?.label || "Bilinmiyor"}
          </Badge>
        );
      },
    },
  ];

  return (
    <AppShell pageId="istifa">
      <ClassicWindow title="Personel İstifa Formu İndirme ve Bildirimler" width="100%">
        <Toolbar>
          <ToolbarButton icon="📥" label="Formu İndir (.docx)" onClick={handleDownload} disabled={downloading} testId="istifa-download-btn" />
          <ToolbarButton
            icon="🧹"
            label="Temizle"
            testId="istifa-clear-btn"
            onClick={() => {
              setForm(blankForm());
              setSearchParams({});
            }}
          />
          <ToolbarSeparator />
          <span className="px-1 text-xs text-slate-600">
            Toplam İstifa Bildirimi: <b className="text-slate-900">{count}</b>
          </span>
        </Toolbar>

        <div className="grid grid-cols-1 items-start gap-4 p-4 xl:grid-cols-[400px_1fr]">
          <GroupBox title="İstifa Bilgileri">
            <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
              <Alert
                size="sm"
                variant="filled"
                color={branchInfo ? "success" : "warning"}
                title={branchLoading ? "Şube adresi yükleniyor..." : branchInfo ? `Şube: ${branchInfo.name}` : "Şube adresi bulunamadı"}
                description={branchLoading ? undefined : branchInfo ? branchInfo.address : "Şube adresi yüklenemedi veya eşleşen şube bulunamadı."}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                color="success"
                onClick={loadBranch}
                loading={branchLoading}
                title="Şube Adresini Tekrar Çek"
                data-testid="istifa-refetch-branch-btn"
              >
                <span className="flex items-center gap-1.5">
                  <RotateCw size={13} /> Adresi Tekrar Çek
                </span>
              </Button>

              <div className="flex flex-col gap-1.5">
                <FormLabel>Personel</FormLabel>
                <Select
                  searchable
                  searchPlaceholder="Personel ara…"
                  placeholder="— Personel Seçiniz —"
                  emptyMessage="Personel bulunamadı"
                  options={employeeOptions}
                  value={form.employeeId}
                  onChange={(e) => setField("employeeId", e.target.value)}
                  data-testid="istifa-employee-select"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <FormLabel>Giriş Tarihi (İşe Başlama)</FormLabel>
                <Input
                  type="date"
                  value={form.hireDate}
                  onChange={(e) => setField("hireDate", e.target.value)}
                  data-testid="istifa-hiredate-input"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <FormLabel>Çıkış Tarihi (Son Çalışma)</FormLabel>
                <Input
                  type="date"
                  value={form.lastWorkDate}
                  onChange={(e) => setField("lastWorkDate", e.target.value)}
                  data-testid="istifa-lastwork-input"
                />
              </div>

              <Button type="button" color="dark" radius="lg" className="!w-full" onClick={handleDownload} loading={downloading}>
                <span className="flex items-center justify-center gap-2">
                  <Download size={16} /> Belgeyi İndir (.docx)
                </span>
              </Button>
            </form>
          </GroupBox>

          <GroupBox title="Geçmiş İstifa Bildirimleri (Admine Gidenler)">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="w-40">
                <Select size="sm" options={FIELDS} value={filter.field} onChange={(e) => setFilter({ ...filter, field: e.target.value })} />
              </div>
              <div className="w-36">
                <Select
                  size="sm"
                  options={OPERATORS}
                  value={filter.operator}
                  onChange={(e) => setFilter({ ...filter, operator: Number(e.target.value) })}
                />
              </div>
              <div className="min-w-[160px] flex-1">
                <Input
                  size="sm"
                  placeholder="Değer..."
                  value={filter.value}
                  onChange={(e) => setFilter({ ...filter, value: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
              <Button size="sm" onClick={handleSearch}>
                <span className="flex items-center gap-1.5">
                  <Search size={14} /> Ara
                </span>
              </Button>
            </div>

            <Table
              size="sm"
              uniqueKey="id"
              columns={columns}
              dataSource={items}
              loading={loading}
              locale={{ emptyText: "Bildirim bulunamadı", emptyHint: "Filtreyi değiştirip tekrar deneyin." }}
            />

            <div className="mt-3 flex justify-end">
              <Pagination
                current={page}
                perPage={size || 10}
                totalItems={count}
                prevButtonText="Önceki"
                nextButtonText="Sonraki"
                buttonProps={{ size: "sm", disabled: loading }}
                onChangePage={handlePageChange}
              />
            </div>
          </GroupBox>
        </div>
      </ClassicWindow>
    </AppShell>
  );
};

export default IstifaPage;
